<template>

  <v-snackbar outlined bottom right class="mr-10 download-manager" min-width="300" max-width="400" color="#727272" :timeout="-1" :value="true">
    <v-toolbar flat dense height="32" class="manager-toolbar">
      <div class="manager-title">Download Manager</div>
      <v-spacer />
      <!-- Clear completed -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn plain icon width="32" v-bind="attrs" v-on="on" @click="clear">
            <v-icon color="#9c9c9c" style="font-size:19px">mdi-cancel</v-icon>
          </v-btn>
        </template>
        <div>Clear completed</div>
      </v-tooltip>

      <!-- Hide manager -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn plain icon width="32" v-bind="attrs" v-on="on" @click="close">
            <v-icon color="#9c9c9c" style="font-size:21px">mdi-close</v-icon>
          </v-btn>
        </template>
        <div>Hide manager</div>
      </v-tooltip>
    </v-toolbar>
    <div class="item-container">
      <div class="mt-2 mb-1 px-4 d-flex align-center justify-left" v-for="(status, itemKey) in downloadStatus" :key="itemKey">
        <div class="mr-4 download-message-text">{{ parseStatusItemLabel(itemKey) }}</div>
        <v-progress-circular
            v-if="status === 'downloading'"
            indeterminate
            :size="15"
            color="primary" />
        <v-icon
            v-else-if="status === 'completed'"
            style="font-size:19px"
            color="green">
          mdi-check-bold
        </v-icon>
      </div>
    </div>
  </v-snackbar>

</template>

<script>
import { mapState } from 'vuex'
const DOWNLOAD_API_BASE_URL = process.env.VUE_APP_DOWNLOAD_API_BASE_URL // data-services
const DATA_API_BASE_URL = process.env.VUE_APP_DATA_API_BASE_URL // data-api

export default {
  name: 'DownloadManager',

  computed: {
    ...mapState('app', {
      downloadStatus: state => state.downloadStatus
    }),
    appName () {
      return this.$store.getters['app/platform']
    }
  },

  methods: {
    // Clear all completed download requests
    clear () {
      this.$store.commit('app/clearCompletedDownloads')
    },

    // Close the download manager
    close () {
      this.$root.closeDownloadManager()
    },

    // Parses the downloadStatus item key into a displayable label
    parseStatusItemLabel (itemKey) {
      const parts = itemKey.split('_')
      if (parts.length === 2) {
        const typeLabel = (parts[1] === 'cinvoice') ? 'Credit' : 'Order'
        return `${typeLabel} Invoice ${parts[0]}`
      } else if (parts.length === 3) {
        const typeLabel = (parts[1] === 'cattachment') ? 'Credit' : 'Order'
        let index = Number(parts[2])
        return `${typeLabel} ${parts[0]} Attachment ${++index}`
      } else {
        return itemKey
      }
    },

    // Set order invoices to download
    setOrderInvoicesToDownload (orders = []) {
      // Notify store
      this.$store.commit('app/setOrderInvoicesToDownload', orders)
      // Perform download request
      for (const order of orders) {
        this.downloadOrderInvoice(order)
      }
    },

    // Set order attachments to download
    setOrderAttachmentsToDownload (order = {}) {
      const attachments = order.attachment_docs
      if (!attachments || this.$root.utils.isEmpty(attachments)) return false

      // Notify store
      this.$store.commit('app/setOrderAttachmentsToDownload', order)
      // Perform download request
      Object.keys(attachments).forEach(itemKey => {
        const info = attachments[itemKey]
        this.downloadOrderAttachment(order, info)
      })
    },

    // Perform the order invoice download request
    async downloadOrderInvoice (order) {
      if (!order || !DOWNLOAD_API_BASE_URL) return false

      const downloadUrl = `${DOWNLOAD_API_BASE_URL}/download/order/${order.id}/invoice`

      try {
        // Perform download request
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/pdf' }
        })

        if (!response.ok) {
          throw new Error('Problem with the network')
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        // Generate file name
        const fileName = (order.is_credit)
          ? `${this.appName}_credit_invoice_${order.order_number}.pdf`
          : `${this.appName}_order_invoice_${order.order_number}.pdf`

        // Create a temporary anchor element
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()

        // Clean up temp anchor
        a.remove()
        window.URL.revokeObjectURL(blobUrl)

        // Notify store of completed download
        this.$store.commit('app/setOrderInvoiceDownloadComplete', order)
      } catch (err) {
        // const errorMessage = err.message || err.text || err
        console.error(`There was an issue downloading order invoice ${order.order_number}`, err)

        // Notify store of download error
        this.$store.commit('app/setOrderInvoiceDownloadError', order)
      }
    },

    // Perform the order attachment doc download request
    async downloadOrderAttachment (order, attachmentInfo) {
      if (!order || !attachmentInfo || !DATA_API_BASE_URL) return false

      const orderIdForAssets = (order.root_order_id) ? order.root_order_id : order.id
      const assetKey = attachmentInfo.asset_key
      const fileType = attachmentInfo.file_type
      const fileName = attachmentInfo.file_name

      const downloadUrl = `${DATA_API_BASE_URL}/order/${orderIdForAssets}/asset/download?asset_key=${assetKey}&file_type=${fileType}&file_name=${fileName}`

      try {
        // Perform download request
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/pdf' }
        })

        if (!response.ok) {
          throw new Error('Problem with the network')
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        // Create a temporary anchor element
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()

        // Clean up temp anchor
        a.remove()
        window.URL.revokeObjectURL(blobUrl)

        // Notify store of completed download
        this.$store.commit('app/setOrderAttachmentDownloadComplete', { order, attachmentInfo })
      } catch (err) {
        // const errorMessage = err.message || err.text || err
        console.error(`There was an issue downloading order attachment ${fileName}`, err)

        // Notify store of download error
        this.$store.commit('app/setOrderAttachmentDownloadError', { order, attachmentInfo })
      }
    }
  }
}
</script>

<style>
  .download-manager .v-snack__content {
    padding: 6px 2px;
  }

  .download-manager .v-snack__wrapper.theme--dark {
    /* background-color: #1e1e1e; */
    background-color: #212121 !important;
  }

  .download-manager .v-toolbar__content {
    /* background-color: #1e1e1e; */
    background-color: #212121 !important;
    padding-right: 8px !important;
  }

  .download-manager .manager-title {
    color: #9c9c9c;
    font-size: 12px;
    text-transform: uppercase !important;
    letter-spacing: 1.08px;
  }

  .download-manager .item-container {
    max-height: 260px;
    overflow-y: scroll;
  }

  .download-manager .download-message-text {
    color: #c9c9c9;
    font-weight: 400;
    padding-top: 2px;
  }
</style>
